import { Globe, Scale, Building2, Landmark, Flag } from "lucide-react";

const policies = [
  {
    name: "Paris Agreement",
    description: "Global framework committing 196 countries to limit warming to 1.5°C. Sets the foundation for national climate targets and carbon market cooperation.",
    icon: Globe,
    color: "bg-forest-50 text-forest-700"
  },
  {
    name: "CBAM (EU)",
    description: "Carbon Border Adjustment Mechanism imposing carbon costs on imports to prevent carbon leakage and ensure fair competition.",
    icon: Scale,
    color: "bg-earth-50 text-earth-700"
  },
  {
    name: "EU ETS",
    description: "World's largest carbon market covering power, industry, and aviation sectors. Cap-and-trade system with declining emission allowances.",
    icon: Building2,
    color: "bg-blue-50 text-blue-700"
  },
  {
    name: "India Carbon Credit Trading Scheme",
    description: "India's national carbon market framework under development, aimed at achieving NDC targets and Net Zero by 2070.",
    icon: Flag,
    color: "bg-orange-50 text-orange-700"
  }
];

const regions = [
  { name: "European Union", flag: "🇪🇺", market: "EU ETS", price: "€90+/tonne" },
  { name: "China", flag: "🇨🇳", market: "China ETS", price: "¥70/tonne" },
  { name: "United Kingdom", flag: "🇬🇧", market: "UK ETS", price: "£85/tonne" },
  { name: "Canada", flag: "🇨🇦", market: "Federal Carbon Pricing", price: "C$80/tonne" },
  { name: "South Korea", flag: "🇰🇷", market: "K-ETS", price: "₩20,000/tonne" },
];

export default function GlobalPolicySection() {
  return (
    <section className="py-16 lg:py-24 bg-slate-50" data-testid="global-policy-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="font-montserrat text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Global Climate Policy
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Key international frameworks and mechanisms driving the carbon economy.
          </p>
        </div>

        {/* Policy Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {policies.map((policy) => (
            <div
              key={policy.name}
              className="bg-white rounded-2xl border border-slate-100 p-6 hover:shadow-lg transition-shadow"
              data-testid={`policy-${policy.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${policy.color}`}>
                  <policy.icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-montserrat font-semibold text-lg text-slate-900 mb-2">
                    {policy.name}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {policy.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Regional Markets */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 lg:p-8">
          <h3 className="font-montserrat font-semibold text-xl text-slate-900 mb-6 text-center">
            Major Carbon Markets Worldwide
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {regions.map((region) => (
              <div
                key={region.name}
                className="text-center p-4 rounded-xl bg-slate-50 hover:bg-forest-50 transition-colors"
              >
                <span className="text-3xl mb-2 block">{region.flag}</span>
                <p className="font-medium text-slate-900 text-sm">{region.name}</p>
                <p className="text-xs text-slate-500 mt-1">{region.market}</p>
                <p className="text-xs font-semibold text-forest-700 mt-1">{region.price}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
